import {
  EIntegrationProvider,
  EMcpAuthMode,
  type EMcpTransport,
  type IIntegrationConfigMcp,
} from "../types";

/**
 * NOTE: NSAI-5354 — compares two MCP URLs as the same server: scheme and host
 * lowercased, one trailing slash dropped, whitespace trimmed. Query and fragment are
 * preserved on purpose — some servers carry a workspace or tenant id there, and
 * dropping it would merge two genuinely different servers.
 *
 * MIRRORED from the server's `normalizeMcpUrlForComparison`
 * (apps/nsai/server/src/integration/mcps/mcps.utils.ts). The server has no workspace
 * dependency on this package (same pattern as the chart / diagram budgets), so the
 * function is duplicated rather than imported. Keep the two in sync: if the client is
 * laxer, a member gets an error at submit time instead of inline guidance; if it is
 * stricter, the form blocks a URL the server would have accepted.
 */
export const normalizeMcpUrl = (url: string): string => {
  const trimmed = url.trim();
  if (!trimmed) return "";

  const withoutTrailingSlash = trimmed.replace(/\/+$/, "");

  try {
    const parsed = new URL(withoutTrailingSlash);
    const path = parsed.pathname.replace(/\/+$/, "");

    return `${parsed.protocol.toLowerCase()}//${parsed.host.toLowerCase()}${path}${parsed.search}${parsed.hash}`;
  } catch {
    // NOTE: A partially typed URL is not an error here — the form is validated
    // separately; this only decides whether it matches a managed server.
    return withoutTrailingSlash.toLowerCase();
  }
};

export interface IFindManagedMcpByUrlProps {
  configs: IIntegrationConfigMcp[] | undefined;
  url: string | undefined;
}

/**
 * NOTE: NSAI-5354 — the organization-managed server matching a URL a member typed
 * into the custom-server form, if any. `configs` is already project-scoped by the
 * server (getIntegrationConfigs filters by project), so a match here means the member
 * can pick this server from the list — which is what makes redirecting them to it
 * correct rather than a dead end.
 */
export const findManagedMcpByUrl = ({
  configs,
  url,
}: IFindManagedMcpByUrlProps): IIntegrationConfigMcp | undefined => {
  const target = normalizeMcpUrl(url ?? "");

  return target
    ? configs?.find((config) => normalizeMcpUrl(config.url) === target)
    : undefined;
};

// NOTE: NSAI-5354 — how one MCP server's tools are namespaced for the model:
// `mcp__<approval-scope hash>__<remote tool name>`. Two connected servers exposing
// the same remote name would otherwise collapse into one entry in the merged tool
// map, silently shadowing one of them.
//
// MIRRORED from the server's MCPS_TOOL_NAME_PREFIX / buildMcpsToolName
// (apps/nsai/server/src/integration/mcps/mcps.utils.ts). Display only on this side:
// the clients never build these names, they only read them back out of a tool call.
const MCP_TOOL_NAME_PREFIX = "mcp__";
const MCP_TOOL_NAME_SEPARATOR = "__";

export interface IParsedMcpToolName {
  scopeHash: string;
  toolName: string;
}

/**
 * NOTE: NSAI-5354 — splits `mcp__<hash>__<remote name>` into its parts so a trace,
 * an approval card or a bot message can show the remote tool name instead of the
 * hash. Returns null for anything else, so callers fall through to their default.
 *
 * Splits on the FIRST separator after the prefix, not the last: a remote tool name
 * may itself contain `__`, the hash never does.
 */
export const parseMcpToolName = (
  toolName: string,
): IParsedMcpToolName | null => {
  if (!toolName.startsWith(MCP_TOOL_NAME_PREFIX)) return null;

  const withoutPrefix = toolName.slice(MCP_TOOL_NAME_PREFIX.length);
  const separatorIndex = withoutPrefix.indexOf(MCP_TOOL_NAME_SEPARATOR);
  if (separatorIndex <= 0) return null;

  const remoteName = withoutPrefix.slice(
    separatorIndex + MCP_TOOL_NAME_SEPARATOR.length,
  );

  return remoteName.length > 0
    ? {
        scopeHash: withoutPrefix.slice(0, separatorIndex),
        toolName: remoteName,
      }
    : null;
};

/**
 * NOTE: NSAI-5354 — the remote tool name, humanized for display
 * (`mcp__a1b2__search_docs` → "Search Docs"). Same treatment the custom HTTP
 * integration names get.
 */
export const formatMcpToolDisplayName = (remoteName: string): string =>
  remoteName
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

export interface IIsMcpOAuthAuthorizeUrlProps {
  provider: EIntegrationProvider | string;
  // NOTE: The auth mode the connect was made with. The organization-server
  // picker sends OAUTH when the picked server is an OAuth one, so both forms
  // state it.
  authMode: EMcpAuthMode | undefined;
  url: string;
}

/**
 * NOTE: NSAI-4148 — did an OAuth MCP connect answer with a sign-in URL rather
 * than a result? MCP is a direct-connection provider: a static scheme's connect
 * finishes inside the request and answers with the caller's own return URL plus
 * `success` / `error`. An OAUTH connect answers with the authorization server's
 * sign-in page, unless it failed before getting there, in which case it answers
 * with an `error` like any other.
 *
 * Gated on the auth mode as well as the URL, so a static connect whose answer
 * happens to state no outcome is never mistaken for a sign-in page and
 * navigated to. Both clients make this decision here.
 */
export const isMcpOAuthAuthorizeUrl = ({
  provider,
  authMode,
  url,
}: IIsMcpOAuthAuthorizeUrlProps): boolean => {
  if (
    provider !== EIntegrationProvider.MCPS ||
    authMode !== EMcpAuthMode.OAUTH
  ) {
    return false;
  }

  try {
    const { searchParams } = new URL(url);

    return (
      searchParams.get("error") !== "true" &&
      searchParams.get("success") !== "true"
    );
  } catch {
    return false;
  }
};

export interface IMcpCustomConnectValues {
  mcpTitle: string;
  mcpUrl: string;
  mcpTransport: EMcpTransport;
  mcpAuthMode: EMcpAuthMode;
  mcpAuthHeaderName?: string;
  mcpOAuthClientId?: string;
  mcpOAuthClientSecret?: string;
  mcpToken?: string;
}

export interface IMcpCustomConnectPayload {
  mcpTitle: string;
  mcpUrl: string;
  mcpTransport: EMcpTransport;
  mcpAuthMode: EMcpAuthMode;
  mcpAuthHeaderName?: string;
  mcpOAuthClientId?: string;
  mcpToken: string;
}

/**
 * NOTE: NSAI-4148 — the custom-server connect payload, carrying only the
 * credential of the scheme the member ended on. The form keeps the OAuth client
 * secret apart from the static token, and the server reads both from
 * `mcpToken`, so this is where one of them is chosen: a bearer token typed
 * before a switch to OAUTH is never sent as a client secret to the
 * authorization server, and a client id or secret left behind by a switch away
 * from OAUTH is never sent at all.
 */
export const buildMcpCustomConnectPayload = ({
  mcpOAuthClientId,
  mcpOAuthClientSecret,
  mcpToken,
  ...connection
}: IMcpCustomConnectValues): IMcpCustomConnectPayload =>
  connection.mcpAuthMode === EMcpAuthMode.OAUTH
    ? {
        ...connection,
        mcpOAuthClientId: mcpOAuthClientId ?? "",
        mcpToken: mcpOAuthClientSecret ?? "",
      }
    : { ...connection, mcpToken: mcpToken ?? "" };
